import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Spin, Row, Col, Modal, Form } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import SearchBar from './SearchBar';
import BookmarkCard from './BookmarkCard';
import { useBookmarks } from '../contexts/BookmarkContext';

const { TextArea } = Input;

const BookmarkList = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState(null);
  const [form] = Form.useForm();
  const { categoryId } = useParams();

  // 使用书签上下文
  const {
    filteredBookmarks,
    isLoading,
    createBookmark,
    updateBookmark,
    deleteBookmark,
    searchBookmarks,
    recordBookmarkClick,
    setCurrentCategory,
    loadBookmarks,
    categoryLabels,
    stats
  } = useBookmarks();

  // 当路由参数改变时更新当前分类
  useEffect(() => {
    if (categoryId) {
      setCurrentCategory(categoryId);
    } else {
      setCurrentCategory('all');
    }
  }, [categoryId, setCurrentCategory]);

  const handleSearch = (value) => {
    searchBookmarks(value);
  };

  const showModal = (bookmark = null) => {
    setEditingBookmark(bookmark);
    
    if (bookmark) {
      // 编辑模式：填充现有数据
      form.setFieldsValue({
        title: bookmark.title,
        url: bookmark.url,
        description: bookmark.description,
        category: bookmark.category,
        tags: bookmark.tags || [],
        isPrivate: bookmark.isPrivate || false
      });
    } else {
      // 新建模式：设置默认值
      form.setFieldsValue({ 
        title: '', 
        url: '', 
        description: '',
        category: categoryId !== 'all' ? categoryId : undefined,
        tags: [],
        isPrivate: false
      });
    }
    
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingBookmark(null);
  };

  const handleSubmit = async (values) => {
    try {
      // 处理标签：将字符串转换为数组
      const processedValues = {
        ...values,
        tags: Array.isArray(values.tags) 
          ? values.tags 
          : values.tags ? values.tags.split(',').map(tag => tag.trim()).filter(Boolean) : []
      };

      if (editingBookmark) {
        await updateBookmark(editingBookmark._id, processedValues);
      } else {
        await createBookmark(processedValues);
      }

      handleCancel();
    } catch (error) {
      // 错误已在Context中处理
      console.error('表单提交失败:', error);
    }
  };

  const handleDelete = (bookmark) => {
    Modal.confirm({
      title: '删除书签',
      content: `确定要删除书签 "${bookmark.title}" 吗？`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteBookmark(bookmark._id);
        } catch (error) {
          // 错误已在Context中处理
          console.error('删除失败:', error);
        }
      },
    });
  };

  const handleBookmarkClick = async (bookmark) => {
    // 记录点击并打开链接
    try {
      await recordBookmarkClick(bookmark._id);
      window.open(bookmark.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      // 即使记录失败也要打开链接
      window.open(bookmark.url, '_blank', 'noopener,noreferrer');
    }
  };

  // 获取分类选项
  const getCategoryOptions = () => {
    return Object.entries(categoryLabels).map(([key, label]) => ({
      value: key,
      label: label
    }));
  };

  // 如果是分类管理页面，不显示书签列表
  if (categoryId === 'categories') {
    return null;
  }

  return (
    <div className="bookmark-container">
      {/* 头部操作区 */}
      <div className="search-wrapper" style={{ 
        marginBottom: 16, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <SearchBar onSearch={handleSearch} />
        </div>
        
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadBookmarks}
            loading={isLoading}
            title="刷新书签"
          >
            刷新
          </Button>
          
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            loading={isLoading}
          >
            添加书签
          </Button>
        </div>
      </div>

      {/* 统计信息 */}
      {stats.total > 0 && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          background: '#f5f5f5', 
          borderRadius: 6,
          fontSize: '14px',
          color: '#666'
        }}>
          <span>
            共 {filteredBookmarks.length} 个书签
            {categoryId && categoryId !== 'all' && ` (${categoryLabels[categoryId] || categoryId})`}
          </span>
          {stats.total !== filteredBookmarks.length && (
            <span> / 总计 {stats.total} 个</span>
          )}
          <span> • 总点击 {stats.totalClicks} 次</span>
        </div>
      )}

      {/* 书签网格 */}
      <Spin spinning={isLoading}>
        {filteredBookmarks.length === 0 && !isLoading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: 48, 
            color: '#999',
            background: '#fafafa',
            borderRadius: 8,
            border: '1px dashed #d9d9d9'
          }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>
              {categoryId && categoryId !== 'all' 
                ? `${categoryLabels[categoryId] || categoryId} 分类暂无书签` 
                : '暂无书签'
              }
            </p>
            <p style={{ fontSize: 14 }}>
              点击 "添加书签" 开始收藏您喜欢的网站
            </p>
          </div>
        ) : (
          <Row 
            gutter={[48, 32]} 
            className="bookmark-grid"
            style={{ margin: '0 -24px' }}
          >
            {filteredBookmarks.map((bookmark) => (
              <Col 
                xs={24} 
                sm={12} 
                md={8} 
                lg={6} 
                xl={6} 
                xxl={4} 
                key={bookmark._id}
                style={{ padding: '0 24px', marginBottom: 24 }}
              >
                <BookmarkCard
                  bookmark={bookmark}
                  onEdit={showModal}
                  onDelete={handleDelete}
                  onClick={handleBookmarkClick}
                />
              </Col>
            ))}
          </Row>
        )}
      </Spin>

      {/* 添加/编辑书签模态框 */}
      <Modal
        title={editingBookmark ? '编辑书签' : '添加书签'}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={form.submit}
        okText="确定"
        cancelText="取消"
        confirmLoading={isLoading}
        width={600}
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[
              { required: true, message: '请输入书签标题！' },
              { max: 100, message: '标题长度不能超过100个字符！' }
            ]}
          >
            <Input placeholder="请输入标题" showCount maxLength={100} />
          </Form.Item>
          
          <Form.Item
            name="url"
            label="网址"
            rules={[
              { required: true, message: '请输入网址！' },
              { type: 'url', message: '请输入有效的网址！' }
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[
              { max: 500, message: '描述长度不能超过500个字符！' }
            ]}
          >
            <TextArea 
              placeholder="请输入描述（可选）" 
              rows={3} 
              showCount 
              maxLength={500}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="分类"
                rules={[{ required: true, message: '请选择分类！' }]}
              >
                <Select placeholder="请选择分类" options={getCategoryOptions()} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isPrivate"
                label="私有书签"
                valuePropName="checked"
              >
                <input type="checkbox" style={{ marginTop: 8 }} />
                <span style={{ marginLeft: 8, fontSize: '14px', color: '#666' }}>
                  仅自己可见
                </span>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="tags"
            label="标签"
            help="多个标签用逗号分隔，如：技术,工具,前端"
          >
            <Input placeholder="标签1,标签2,标签3" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BookmarkList; 